import type { Input as BaseUIInput } from '@base-ui/react/input'
import type {
  ComponentProps,
  MouseEvent,
  PointerEvent,
  ReactNode,
  Ref,
} from 'react'

export const InputSize = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const

export type InputSize = (typeof InputSize)[keyof typeof InputSize]

export const InputVariant = {
  Default: 'default',
  Inline: 'inline',
} as const

export type InputVariant = (typeof InputVariant)[keyof typeof InputVariant]

export const InputSlotPosition = {
  Left: 'left',
  Right: 'right',
} as const

export type InputSlotPosition =
  (typeof InputSlotPosition)[keyof typeof InputSlotPosition]

type BaseUIInputProps = ComponentProps<typeof BaseUIInput>

export interface InputContainerProps {
  className?: string
  size?: InputSize
  invalid?: boolean
  disabled?: boolean
  leftSection?: ReactNode
  rightSection?: ReactNode
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
  /** Called on pointerdown in a non-interactive area (e.g. padding, slots).
   *  Call `event.preventDefault()` to suppress the subsequent mousedown/click. */
  onDeadAreaPointerDown?: (
    input: HTMLInputElement,
    event: PointerEvent<HTMLDivElement>,
  ) => void
  /** Called on click in a non-interactive area when the container has no inner
   *  `<input>` to focus (e.g. a button-trigger Select). Lets the consumer
   *  forward dead-area clicks to its own trigger. */
  onDeadAreaClick?: (event: MouseEvent<HTMLDivElement>) => void
}

export interface InputProps
  extends Omit<BaseUIInputProps, 'className' | 'size'> {
  /** CSS class names for the container */
  className?: string
  /** CSS class names for the input element */
  inputClassName?: string
  size?: InputSize
  /** `inline` uses the ghost `Select.Input` chrome so the text sits in the surrounding layout. */
  variant?: InputVariant
  invalid?: boolean
  /** Content to render before the input */
  leftSection?: ReactNode
  /** Content to render after the input */
  rightSection?: ReactNode
  ref?: Ref<HTMLInputElement>
}
